import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Save, ArrowLeft, Upload, Image as ImageIcon, CheckCircle, AlertCircle, Copy } from 'lucide-react';
import { productsService } from '../../services/productsService';
import { brandsService } from '../../services/brandsService';
import { categoriesService } from '../../services/categoriesService';
import iconsService from '../../services/iconsService';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { ProductFlyerLayout, NEUTRALNI_PODKLAD_IKONY } from '../../components/product/ProductFlyerLayout';
import { useAuthStore } from '../../store/authStore';
import { Product } from '../../types';
import DuplicateEanDialog from '../../components/product/DuplicateEanDialog';
import { getProductImageUrl } from '../../utils/helpers';

// Obrázek produktu se v letáku vykresluje do ČTVERCOVÉ plochy (1:1, viz pdf.service
// renderProductSlot). Doporučený rozměr pro nahrání je čtverec (např. 1000×1000 px).
const PRODUCT_IMAGE_RATIO = 1; // 1:1
const PRODUCT_IMAGE_RATIO_TOLERANCE = 0.05;
const PRODUCT_IMAGE_RECOMMENDED = '1000×1000 px';

const getImageDimensions = (src: string): Promise<{ w: number; h: number }> =>
  new Promise((resolve, reject) => {
    const img = new window.Image();
    img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
    img.onerror = reject;
    img.src = src;
  });

// Compare two money values tolerantly: coerce to number and match on whole cents.
// Using a plain `===` was fragile - when a copied product carries its price through the URL
// (toString -> parseFloat) a numerically identical price could read as a mismatch (type/precision),
// while the same product reloaded in edit (clean numbers from the API) read as a match.
const isSameMoney = (a: unknown, b: unknown): boolean => {
  const na = Number(a);
  const nb = Number(b);
  return Number.isFinite(na) && Number.isFinite(nb) && Math.round(na * 100) === Math.round(nb * 100);
};

export const ProductFormPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEdit = !!id;
  const [searchParams] = useSearchParams();

  const [formData, setFormData] = useState({
    ean: '',
    name: '',
    description: '',
    supplierNote: '',
    brandId: '',
    categoryId: '',
    subcategoryId: '',
    installationType: '',
    price: 0,
    originalPrice: 0,
    imageData: '',
    imageMimeType: '',
    iconIds: [] as string[],
  });
  const [imagePreview, setImagePreview] = useState<string>('');
  const [isIconModalOpen, setIsIconModalOpen] = useState(false);
  const [iconSearch, setIconSearch] = useState('');
  const [activeIconTab, setActiveIconTab] = useState<'energy' | 'other'>('other');
  const [isInActiveFlyer, setIsInActiveFlyer] = useState(false);
  const [eanValidation, setEanValidation] = useState<{
    eanFound: boolean | null;
    priceMatch: boolean | null;
    originalPriceMatch: boolean | null;
    isLoading: boolean;
  }>({
    eanFound: null,
    priceMatch: null,
    originalPriceMatch: null,
    isLoading: false,
  });
  const [duplicateEanDialog, setDuplicateEanDialog] = useState<{
    isOpen: boolean;
    existingProduct: Product | null;
    count: number;
    mode: 'blocking' | 'informational';
  }>({
    isOpen: false,
    existingProduct: null,
    count: 0,
    mode: 'blocking',
  });
  const [pendingFormData, setPendingFormData] = useState<any>(null);

  const { data: brands = [] } = useQuery({
    queryKey: ['brands', 'my'],
    queryFn: () => brandsService.getMyBrands(),
  });

  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: () => categoriesService.getAllCategories(),
  });

  const { data: subcategories = [] } = useQuery({
    queryKey: ['subcategories', formData.categoryId],
    queryFn: () => categoriesService.getSubcategories(formData.categoryId),
    enabled: !!formData.categoryId,
  });

  const { data: icons = [] } = useQuery({
    queryKey: ['icons'],
    queryFn: () => iconsService.getAllIcons(),
  });

  // Ikony jsou bílý piktogram na průhledném, na bílém výběru by nebyly vidět. Podklad
  // stejný jako v náhledu (ProductFlyerLayout): barva značky, u prodejce a bez barvy šedá.
  // Jen ikony s useBrandColor - energetické štítky se nepodbarvují.
  const { user } = useAuthStore();
  const barvaZnacky = user?.role === 'end_user' ? null : brands.find(b => b.id === formData.brandId)?.color;
  const podkladIkony = (icon: { useBrandColor?: boolean }) =>
    icon.useBrandColor ? { backgroundColor: barvaZnacky || NEUTRALNI_PODKLAD_IKONY } : undefined;

  const { data: product } = useQuery({
    queryKey: ['products', id],
    queryFn: () => productsService.getProduct(id!),
    enabled: isEdit,
  });

  // Porovná uložené ceny s ERP a jen nastaví značky u cen – nic v formuláři nepřepisuje.
  // Volá se při otevření existujícího produktu i kopie (ta nese ceny z originálu,
  // které se mezitím v ERP mohly změnit).
  const overCenyVErp = async (ean: string, price: number, originalPrice: number) => {
    if (!ean) return;
    setEanValidation({
      eanFound: null,
      priceMatch: null,
      originalPriceMatch: null,
      isLoading: true,
    });
    try {
      const result = await productsService.validateEAN(ean, price, originalPrice);

      // Kopie přebírá typ z originálu, a ten může být prázdný (produkty z doby, kdy se typ
      // neukládal). Typ je jen otisk z ERP, takže ho u nového produktu převezmeme odsud –
      // stejně jako tlačítko ERP.
      if (!isEdit) {
        setFormData((prev) => ({ ...prev, installationType: (result.found && result.erpInstallationType) || '' }));
      }

      // Check individual price matches
      const priceMatch = result.found && isSameMoney(result.erpPrice, price);
      const originalPriceMatch = result.found && isSameMoney(result.erpOriginalPrice, originalPrice);

      setEanValidation({
        eanFound: result.found,
        priceMatch,
        originalPriceMatch,
        isLoading: false,
      });
    } catch (error) {
      console.error('Error validating EAN:', error);
      setEanValidation({
        eanFound: false,
        priceMatch: false,
        originalPriceMatch: false,
        isLoading: false,
      });
    }
  };

  useEffect(() => {
    if (product) {
      setFormData({
        ean: product.eanCode,
        name: product.name,
        description: product.description || '',
        supplierNote: (product as any).supplierNote || '',
        brandId: product.brandId,
        categoryId: (product as any).categoryId || '',
        subcategoryId: (product as any).subcategoryId || '',
        installationType: (product as any).installationType || '',
        price: product.price,
        originalPrice: product.originalPrice || 0,
        imageData: '',
        imageMimeType: '',
        iconIds: product.icons?.map(i => i.id) || [],
      });
      // Set preview from API endpoint if product has image. Use the shared helper so the
      // URL respects REACT_APP_API_URL (production serves via the IIS proxy, NOT localhost:4000)
      // and gets an updatedAt cache-buster so a replaced image isn't masked by the 1-year cache.
      if (id) {
        setImagePreview(getProductImageUrl(id, product.updatedAt));
      }

      // Check if product is in active approved flyer
      setIsInActiveFlyer((product as any).isInActiveFlyer || false);

      // Validate EAN when loading existing product
      overCenyVErp(product.eanCode, product.price, product.originalPrice || 0);
    } else if (!isEdit && searchParams.get('copyFrom')) {
      // Načítáme kopii produktu - data ze sessionStorage
      const copyFromId = searchParams.get('copyFrom');

      console.log('=== NAČÍTÁNÍ KOPIE PRODUKTU V useEffect ===');
      console.log('copyFromId:', copyFromId);

      // Načteme data ze sessionStorage
      const imageData = sessionStorage.getItem('copyProductImage');
      const imageMimeType = sessionStorage.getItem('copyProductImageMimeType');

      console.log('Data ze sessionStorage:');
      console.log('- imageData existuje:', !!imageData);
      console.log('- imageData délka:', imageData?.length);
      console.log('- imageData náhled:', imageData?.substring(0, 50));
      console.log('- imageMimeType:', imageMimeType);

      // Nastavíme formData s daty ze sessionStorage
      setFormData({
        ean: searchParams.get('ean') || '',
        name: searchParams.get('name') || '',
        description: searchParams.get('description') || '',
        supplierNote: searchParams.get('supplierNote') || '',
        brandId: searchParams.get('brandId') || '',
        categoryId: searchParams.get('categoryId') || '',
        subcategoryId: searchParams.get('subcategoryId') || '',
        installationType: searchParams.get('installationType') || '',
        price: parseFloat(searchParams.get('price') || '0'),
        originalPrice: parseFloat(searchParams.get('originalPrice') || '0'),
        imageData: imageData || '',
        imageMimeType: imageMimeType || '',
        iconIds: searchParams.get('iconIds') ? JSON.parse(searchParams.get('iconIds')!) : [],
      });

      console.log('FormData nastavena s imageData délkou:', imageData?.length || 0);

      // Ceny kopie jsou z originálu – hned ukázat, jestli ještě sedí s ERP.
      overCenyVErp(
        searchParams.get('ean') || '',
        parseFloat(searchParams.get('price') || '0'),
        parseFloat(searchParams.get('originalPrice') || '0'),
      );

      // Nastavíme preview
      if (imageData && imageMimeType) {
        const imageUrl = getProductImageUrl(copyFromId!);
        setImagePreview(imageUrl);
        console.log('Preview nastaven na:', imageUrl);
      }

      // DŮLEŽITÉ: Resetujeme isInActiveFlyer na false, protože kopie je nový produkt
      setIsInActiveFlyer(false);

      // Vymažeme data ze sessionStorage po použití
      sessionStorage.removeItem('copyProductImage');
      sessionStorage.removeItem('copyProductImageMimeType');
      console.log('SessionStorage vyčištěn');
    } else if (brands.length > 0 && !formData.brandId) {
      setFormData(prev => ({ ...prev, brandId: brands[0].id }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product, brands, searchParams.get('copyFrom')]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Prosím vyberte obrázek');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Obrázek je příliš velký. Maximální velikost je 5MB.');
      return;
    }

    const input = e.target; // uchovat referenci pro případný reset po odmítnutí

    // Read file and convert to base64
    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64String = reader.result as string;

      // Měkká kontrola poměru stran - obrázek produktu se v letáku zobrazuje ve
      // čtvercové ploše (1:1). Když je výrazně mimo, upozornit a nechat rozhodnout.
      try {
        const { w, h } = await getImageDimensions(base64String);
        // Symetrická relativní odchylka (vůči průměru obou poměrů) – konzistentní s kontrolou
        // u promo obrázků; nezáleží, který poměr je referenční.
        const imgRatio = w / h;
        const off = Math.abs(imgRatio - PRODUCT_IMAGE_RATIO) / ((imgRatio + PRODUCT_IMAGE_RATIO) / 2);
        if (off > PRODUCT_IMAGE_RATIO_TOLERANCE) {
          const ok = window.confirm(
            `Obrázek má poměr stran ${(w / h).toFixed(2)} : 1 (${w}×${h} px). ` +
            `Obrázek produktu se v letáku zobrazuje ve čtvercové ploše (doporučeno 1 : 1, např. ${PRODUCT_IMAGE_RECOMMENDED}) — ` +
            `jinak se zmenší a vzniknou prázdné okraje. Chcete přesto tento obrázek použít?`
          );
          if (!ok) {
            if (input) input.value = '';
            return;
          }
        }
      } catch {
        // Rozměry se nepodařilo načíst - kontrolu přeskočíme, nahrání nezablokujeme.
      }

      // Remove the data:image/...;base64, prefix
      const base64Data = base64String.split(',')[1];

      setFormData({
        ...formData,
        imageData: base64Data,
        imageMimeType: file.type,
      });
      setImagePreview(base64String); // Use full data URL for preview
    };
    reader.readAsDataURL(file);
  };

  const handleErpValidation = async () => {
    if (!formData.ean || formData.ean.length < 8) {
      return;
    }

    setEanValidation({
      eanFound: null,
      priceMatch: null,
      originalPriceMatch: null,
      isLoading: true,
    });

    try {
      const result = await productsService.validateEAN(
        formData.ean,
        formData.price,
        formData.originalPrice || 0,
      );

      // Prefill fields from Helios/ERP when creating a NEW product - each field only if it
      // is still empty (brand excepted, see below), so it never overwrites what the user (or a copy) already filled, and
      // never touches an existing product being edited. Restored from the pre-2026-07-14 build.
      let effectivePrice = formData.price;
      let effectiveOriginalPrice = formData.originalPrice || 0;

      if (!isEdit && result.found) {
        if (formData.price === 0 && result.erpPrice !== undefined) {
          effectivePrice = result.erpPrice;
        }
        if ((formData.originalPrice || 0) === 0 && result.erpOriginalPrice !== undefined) {
          effectiveOriginalPrice = result.erpOriginalPrice;
        }
        setFormData((prev) => {
          const next = { ...prev };
          if (!next.name && result.erpProductName) {
            next.name = result.erpProductName;
          }
          // Značka se na rozdíl od ostatních polí přepíše vždy: nový produkt ji má předem
          // nastavenou na první značku ze seznamu, takže „jen když je prázdná" by nenastalo.
          // Jen pokud ERP značku zná a uživatel k ní má přístup; jinak zůstane jeho volba.
          if (result.erpBrand) {
            const brand = brands.find((b) => b.name.toLowerCase() === result.erpBrand!.toLowerCase());
            if (brand) next.brandId = brand.id;
          }
          if (next.price === 0 && result.erpPrice !== undefined) {
            next.price = result.erpPrice;
          }
          if ((next.originalPrice || 0) === 0 && result.erpOriginalPrice !== undefined) {
            next.originalPrice = result.erpOriginalPrice;
          }
          if (!next.categoryId && result.erpCategoryCode) {
            const category = categories.find((c) => c.mssqlCode === result.erpCategoryCode);
            if (category) next.categoryId = category.id;
          }
          return next;
        });
      }

      // Typ spotřebiče je jen otisk z ERP (v aplikaci se needituje), proto se u nového
      // produktu přepíše vždy - i prázdnou hodnotou, když ho ERP nemá. Uloží ho stejně
      // backend sám podle EAN; tady jde o to, aby ho uživatel viděl předem.
      if (!isEdit) {
        setFormData((prev) => ({ ...prev, installationType: (result.found && result.erpInstallationType) || '' }));
      }

      // Coloring: compare ERP prices to the effective (post-prefill) values (tolerant compare).
      const priceMatch = result.found && isSameMoney(result.erpPrice, effectivePrice);
      const originalPriceMatch = result.found && isSameMoney(result.erpOriginalPrice, effectiveOriginalPrice);

      setEanValidation({
        eanFound: result.found,
        priceMatch,
        originalPriceMatch,
        isLoading: false,
      });
    } catch (error) {
      console.error('Error validating EAN:', error);
      setEanValidation({
        eanFound: false,
        priceMatch: false,
        originalPriceMatch: false,
        isLoading: false,
      });
    }
  };

  const saveMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      console.log('=== ZAČÁTEK UKLÁDÁNÍ PRODUKTU ===');
      console.log('FormData:', {
        ean: data.ean,
        name: data.name,
        brandId: data.brandId,
        hasImageData: !!data.imageData,
        imageMimeType: data.imageMimeType,
        imageDataLength: data.imageData?.length,
        imageDataPreview: data.imageData?.substring(0, 50)
      });

      const payload: any = {
        name: data.name,
        description: data.description,
        supplierNote: data.supplierNote || undefined,
        price: data.price,
        originalPrice: data.originalPrice,
        iconIds: data.iconIds,
        categoryId: data.categoryId || undefined,
        subcategoryId: data.subcategoryId || undefined,
      };

      // Only include eanCode and brandId when creating (not when editing)
      if (!isEdit) {
        payload.eanCode = data.ean;  // Backend expects eanCode
        payload.brandId = data.brandId;
      }

      // Only include image data if a new file was uploaded
      if (data.imageData && data.imageMimeType) {
        console.log('Přidávám imageData do payload');
        payload.imageData = data.imageData;
        payload.imageMimeType = data.imageMimeType;
      } else {
        console.log('NEPŘIDÁVÁM imageData - data.imageData:', !!data.imageData, 'data.imageMimeType:', data.imageMimeType);
      }

      console.log('Finální payload:', {
        ...payload,
        imageData: payload.imageData ? `${payload.imageData.substring(0, 50)}... (${payload.imageData.length} znaků)` : 'ŽÁDNÁ DATA',
        imageMimeType: payload.imageMimeType || 'ŽÁDNÝ MIME TYPE'
      });

      if (isEdit && id) {
        return productsService.updateProduct(id, payload);
      } else {
        return productsService.createProduct(payload);
      }
    },
    onSuccess: async (savedProduct) => {
      console.log('=== ONSUCCESS CALLBACK ===');
      console.log('Product saved successfully:', savedProduct);
      console.log('savedProduct má id:', savedProduct?.id);
      console.log('isEdit:', isEdit);
      console.log('Podmínka pro navigaci (!isEdit && savedProduct && savedProduct.id):', !isEdit && savedProduct && savedProduct.id);

      queryClient.invalidateQueries({ queryKey: ['products'] });

      // Navigate to the products list after creating a new product,
      // so the freshly created product is visible in the repo.
      if (!isEdit && savedProduct && savedProduct.id) {
        console.log('NAVIGACE - Přecházím na seznam produktů (/products)');
        // Use replace to avoid adding the form to history, making the flow smoother
        navigate('/products', { replace: true });
        return; // Exit early, don't validate EAN yet
      }

      // Validate EAN after saving (only for edits)
      if (formData.ean) {
        setEanValidation({
          eanFound: null,
          priceMatch: null,
          originalPriceMatch: null,
          isLoading: true,
        });
        try {
          const result = await productsService.validateEAN(
            formData.ean,
            formData.price,
            formData.originalPrice,
          );

          // Check individual price matches
          const priceMatch = result.found && isSameMoney(result.erpPrice, formData.price);
          const originalPriceMatch = result.found && isSameMoney(result.erpOriginalPrice, formData.originalPrice || 0);

          setEanValidation({
            eanFound: result.found,
            priceMatch,
            originalPriceMatch,
            isLoading: false,
          });
        } catch (error) {
          console.error('Error validating EAN:', error);
          setEanValidation({
            eanFound: false,
            priceMatch: false,
            originalPriceMatch: false,
            isLoading: false,
          });
        }
      }
    },
    onError: (error: any) => {
      console.error('Error saving product:', error);
      // Zpráva z backendu (class-validator vrací pole hlášek), fallback na axios message.
      const data = error?.response?.data?.message;
      const msg = Array.isArray(data) ? data.join('\n') : (data || error?.message || 'Neznámá chyba');
      alert('Chyba při ukládání produktu: ' + msg);
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted', formData);

    // If editing, skip duplicate check
    if (isEdit) {
      saveMutation.mutate(formData);
      return;
    }

    // Obrázek je u nového produktu povinný - bez něj by v letáku zůstalo prázdné místo.
    // Při úpravě stačí ten stávající, nový soubor se vybírat nemusí.
    if (!isEdit && !formData.imageData) {
      alert('Vyberte obrázek produktu – bez něj produkt nelze založit.');
      return;
    }

    // Check for duplicate EAN only when creating new product
    if (formData.ean && formData.ean.trim() !== '') {
      try {
        const duplicateCheck = await productsService.checkDuplicateEan(formData.ean);

        if (duplicateCheck.exists && duplicateCheck.latestProduct) {
          // Show confirmation dialog (blocking mode)
          setDuplicateEanDialog({
            isOpen: true,
            existingProduct: duplicateCheck.latestProduct,
            count: duplicateCheck.count,
            mode: 'blocking',
          });
          setPendingFormData(formData);
          return; // Don't submit yet
        }

        // EAN existuje u JINÉ značky (ke které nemá přístup) – otevřít nelze; informovat a nechat rozhodnout.
        if (!duplicateCheck.exists && duplicateCheck.existsInOtherBrand) {
          const ok = window.confirm(
            `Tento EAN už používá jiná značka${duplicateCheck.otherBrandName ? ` „${duplicateCheck.otherBrandName}"` : ''}, ke které nemáte přístup – existující produkt nelze otevřít. Přesto vytvořit nový produkt pod vaší značkou?`
          );
          if (!ok) return;
        }
      } catch (error) {
        console.error('Error checking duplicate EAN:', error);
        // Continue with submission even if check fails
      }
    }

    // No duplicates found or EAN is empty, proceed with submission
    saveMutation.mutate(formData);
  };

  const handleDuplicateEanContinue = () => {
    // User confirmed to continue despite duplicate
    setDuplicateEanDialog({ isOpen: false, existingProduct: null, count: 0, mode: 'blocking' });
    if (pendingFormData) {
      saveMutation.mutate(pendingFormData);
      setPendingFormData(null);
    }
  };

  const handleDuplicateEanCancel = () => {
    // User cancelled, close dialog
    setDuplicateEanDialog({ isOpen: false, existingProduct: null, count: 0, mode: 'blocking' });
    setPendingFormData(null);
  };

  const handleDuplicateEanAbort = () => {
    // User aborted the whole action - close dialog and leave the form (abandon product creation)
    setDuplicateEanDialog({ isOpen: false, existingProduct: null, count: 0, mode: 'blocking' });
    setPendingFormData(null);
    navigate('/products');
  };

  const handleEanBlur = async () => {
    // Skip check if editing existing product or EAN is empty
    if (isEdit || !formData.ean || formData.ean.trim() === '') {
      return;
    }

    // Check for duplicate EAN on blur
    try {
      const duplicateCheck = await productsService.checkDuplicateEan(formData.ean);

      if (duplicateCheck.exists && duplicateCheck.latestProduct) {
        // Show info dialog (informational mode)
        setDuplicateEanDialog({
          isOpen: true,
          existingProduct: duplicateCheck.latestProduct,
          count: duplicateCheck.count,
          mode: 'informational',
        });
        // Don't set pendingFormData - this is just informational, not blocking submit
      } else if (duplicateCheck.existsInOtherBrand) {
        // EAN patří jiné značce (ke které nemá přístup) – konzistentně informovat (jinak by se nic neukázalo).
        alert(`Tento EAN už používá jiná značka${duplicateCheck.otherBrandName ? ` „${duplicateCheck.otherBrandName}"` : ''}, ke které nemáte přístup. Existující produkt nelze otevřít.`);
      }
    } catch (error) {
      console.error('Error checking duplicate EAN on blur:', error);
      // Silently fail - don't block user
    }
  };

  const handleCreateCopy = async () => {
    console.log('=== VYTVÁŘENÍ KOPIE PRODUKTU ===');
    console.log('ID produktu:', id);

    // Načteme obrázek před navigací (helper respektuje REACT_APP_API_URL / IIS proxy)
    const imageUrl = getProductImageUrl(id!);
    console.log('URL obrázku:', imageUrl);

    try {
      const axios = (await import('axios')).default;
      console.log('Stahování obrázku...');
      const response = await axios.get(imageUrl, { responseType: 'blob' });
      const blob = response.data;
      console.log('Obrázek stažen, velikost blob:', blob.size, 'bytes, typ:', blob.type);

      // Konvertujeme blob na base64
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        const base64Data = base64String.split(',')[1];

        console.log('Base64 konverze dokončena:');
        console.log('- Délka base64:', base64Data.length);
        console.log('- Náhled:', base64Data.substring(0, 50));
        console.log('- MIME type:', blob.type);

        // Uložíme data obrázku do sessionStorage
        sessionStorage.setItem('copyProductImage', base64Data);
        sessionStorage.setItem('copyProductImageMimeType', blob.type);
        console.log('Data uložena do sessionStorage');

        // Ověříme, že se data opravdu uložila
        const verify = sessionStorage.getItem('copyProductImage');
        console.log('Ověření uložení - délka dat v sessionStorage:', verify?.length);

        // Navigujeme s ostatními daty
        const queryParams = new URLSearchParams({
          copyFrom: id!,
          ean: formData.ean,
          name: formData.name,
          description: formData.description,
          supplierNote: formData.supplierNote,
          brandId: formData.brandId,
          categoryId: formData.categoryId,
          subcategoryId: formData.subcategoryId,
          installationType: formData.installationType,
          price: formData.price.toString(),
          originalPrice: formData.originalPrice.toString(),
          iconIds: JSON.stringify(formData.iconIds),
        });

        console.log('Navigace na novou stránku...');
        navigate(`/products/new?${queryParams.toString()}`);
      };

      reader.onerror = (error) => {
        console.error('Chyba při čtení FileReader:', error);
      };

      reader.readAsDataURL(blob);
    } catch (error) {
      console.error('Chyba při načítání obrázku:', error);
      // I když se obrázek nepodaří načíst, můžeme pokračovat bez něj
      const queryParams = new URLSearchParams({
        copyFrom: id!,
        ean: formData.ean,
        name: formData.name,
        description: formData.description,
        supplierNote: formData.supplierNote,
        brandId: formData.brandId,
        categoryId: formData.categoryId,
        subcategoryId: formData.subcategoryId,
        price: formData.price.toString(),
        originalPrice: formData.originalPrice.toString(),
        iconIds: JSON.stringify(formData.iconIds),
      });

      navigate(`/products/new?${queryParams.toString()}`);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="mb-6 flex items-center">
        <Button variant="outline" onClick={() => navigate('/products')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Zpět na produkty
        </Button>
        <h1 className="text-3xl font-bold text-gray-900 flex-1 text-center">
          {isEdit ? 'Upravit produkt' : 'Nový produkt'}
        </h1>
      </div>

      {isInActiveFlyer && (
        <div className="mb-4 bg-yellow-50 border-l-4 border-yellow-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-yellow-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                <strong>Produkt je v letáku, který čeká na schválení, je schválený nebo aktivní.</strong> Editace je zakázána, dokud platnost letáku nevyprší – jinak by šlo změnit cenu za zády schvalovacího procesu. Potřebujete-li jinou cenu, založte nový produkt nebo jeho kopii.
              </p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="grid gap-8" style={{ gridTemplateColumns: '2fr 1fr' }}>
          <div className="space-y-4 bg-white rounded-lg shadow p-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">EAN kód *</label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Input
                      value={formData.ean}
                      onChange={(e) => setFormData({ ...formData, ean: e.target.value })}
                      onBlur={handleEanBlur}
                      required
                      pattern="[0-9]{8,13}"
                      title="EAN kód musí mít 8-13 číslic"
                      disabled={isEdit}
                    />
                    {eanValidation.isLoading && (
                      <div className="absolute right-3 top-2 text-gray-400">
                        <div className="animate-spin h-5 w-5 border-2 border-gray-400 border-t-transparent rounded-full"></div>
                      </div>
                    )}
                    {!eanValidation.isLoading && eanValidation.eanFound === true && (
                      <div className="absolute right-3 top-2 text-green-600">
                        <CheckCircle className="h-5 w-5" />
                      </div>
                    )}
                    {!eanValidation.isLoading && eanValidation.eanFound === false && (
                      <div className="absolute right-3 top-2 text-red-600">
                        <AlertCircle className="h-5 w-5" />
                      </div>
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleErpValidation}
                    disabled={!formData.ean || formData.ean.length < 8 || eanValidation.isLoading}
                    className="mt-0"
                  >
                    ERP
                  </Button>
                </div>
              </div>

              <Input
                label="Název produktu *"
                maxLength={150}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                disabled={isInActiveFlyer}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Značka *</label>
                <select
                  value={formData.brandId}
                  onChange={(e) => setFormData({ ...formData, brandId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  required
                  disabled={isInActiveFlyer}
                >
                  <option value="">Vyberte značku</option>
                  {brands.map((brand) => (
                    <option key={brand.id} value={brand.id}>{brand.name}</option>
                  ))}
                </select>
              </div>

              <Input
                label="Poznámka dodavatele"
                value={formData.supplierNote}
                onChange={(e) => setFormData({ ...formData, supplierNote: e.target.value })}
                maxLength={100}
                placeholder="Interní poznámka (max. 100 znaků)"
                disabled={isInActiveFlyer}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Kategorie</label>
                <select
                  value={formData.categoryId}
                  onChange={(e) => {
                    setFormData({ ...formData, categoryId: e.target.value, subcategoryId: '' });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  disabled={isInActiveFlyer}
                >
                  <option value="">Vyberte kategorii</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>{category.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Podkategorie</label>
                <select
                  value={formData.subcategoryId}
                  onChange={(e) => setFormData({ ...formData, subcategoryId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  disabled={!formData.categoryId || isInActiveFlyer}
                >
                  <option value="">Vyberte podkategorii</option>
                  {subcategories.map((subcategory) => (
                    <option key={subcategory.id} value={subcategory.id}>{subcategory.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Typ spotřebiče jen k nahlédnutí - bere se z ERP (Helios _Oresi_SubType),
                v aplikaci se needituje. Chybí-li u kategorie, která ho rozlišuje,
                oprava patří do Heliosu (docs/navrh-Umisteni-a-Podkategorie.md). */}
            {(categories.find((c) => c.id === formData.categoryId)?.requiresInstallationType || formData.installationType) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Typ spotřebiče (z ERP)</label>
                {formData.installationType ? (
                  <div className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-100 text-gray-700">
                    {formData.installationType === 'BUILT_IN' ? 'Vestavné spotřebiče' : 'Volně stojící spotřebiče'}
                  </div>
                ) : (
                  <div className="w-full px-3 py-2 border border-orange-200 rounded-lg bg-orange-50 text-orange-800 text-sm">
                    {isEdit
                      ? 'Typ zatím není uložený – při uložení se doplní z ERP.'
                      : eanValidation.eanFound === null
                      ? 'Doplní se z ERP po ověření EAN.'
                      : eanValidation.eanFound === false
                      ? 'Typ se zjistí z ERP až po ověření EAN – EAN zatím v ERP nenalezen.'
                      : 'V ERP chybí typ (vestavné / volně stojící). Kategorie ho vyžaduje, takže produkt nepůjde uložit – doplňte ho na kartě zboží v Heliosu a produkt založte znovu.'}
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="relative">
                <Input
                  label="Cena *"
                  type="number"
                  step="any"
                  min="0"
                  className="pr-14"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
                  required
                  disabled={isInActiveFlyer}
                />
                {eanValidation.isLoading && (
                  <div className="absolute right-8 top-9 pointer-events-none text-gray-400">
                    <div className="animate-spin h-5 w-5 border-2 border-gray-400 border-t-transparent rounded-full"></div>
                  </div>
                )}
                {!eanValidation.isLoading && eanValidation.priceMatch === true && (
                  <div className="absolute right-8 top-9 pointer-events-none text-green-600">
                    <CheckCircle className="h-5 w-5" />
                  </div>
                )}
                {!eanValidation.isLoading && eanValidation.priceMatch === false && (
                  <div className="absolute right-8 top-9 pointer-events-none text-red-600">
                    <AlertCircle className="h-5 w-5" />
                  </div>
                )}
              </div>

              <div className="relative">
                <Input
                  label="Původní cena"
                  type="number"
                  step="any"
                  min="0"
                  className="pr-14"
                  value={formData.originalPrice}
                  onChange={(e) => setFormData({ ...formData, originalPrice: parseFloat(e.target.value) || 0 })}
                  disabled={isInActiveFlyer}
                />
                {eanValidation.isLoading && (
                  <div className="absolute right-8 top-9 pointer-events-none text-gray-400">
                    <div className="animate-spin h-5 w-5 border-2 border-gray-400 border-t-transparent rounded-full"></div>
                  </div>
                )}
                {!eanValidation.isLoading && eanValidation.originalPriceMatch === true && (
                  <div className="absolute right-8 top-9 pointer-events-none text-green-600">
                    <CheckCircle className="h-5 w-5" />
                  </div>
                )}
                {!eanValidation.isLoading && eanValidation.originalPriceMatch === false && (
                  <div className="absolute right-8 top-9 pointer-events-none text-red-600">
                    <AlertCircle className="h-5 w-5" />
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Popis produktu (max. 16 vizuálních řádků)
              </label>
              <div className="relative border-2 border-gray-300 rounded-lg overflow-hidden bg-white focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-200" style={{ width: '350px', maxWidth: '100%' }}>
                <div
                  className="relative"
                  style={{
                    height: 'calc(1rem * 1.5 * 16 + 16px)', // 16 lines with normal readable font + padding
                  }}
                >
                  {/* Bullets layer with invisible text for proper line wrapping */}
                  <div
                    className="absolute inset-0 pointer-events-none overflow-hidden text-base"
                    style={{
                      zIndex: 1,
                      padding: '8px',
                      lineHeight: '1.5rem'
                    }}
                  >
                    {(formData.description ? formData.description.split('\n') : ['']).map((line, index) => (
                      <div key={index} className="flex items-start" style={{ gap: '8px' }}>
                        <span className="text-black font-bold" style={{ width: '16px', flexShrink: 0 }}>•</span>
                        <span className="flex-1 invisible" style={{ wordBreak: 'break-word' }}>{line || ' '}</span>
                      </div>
                    ))}
                  </div>

                  {/* Actual textarea - with padding to align with bullets */}
                  <textarea
                    value={formData.description}
                    maxLength={1000}
                    onChange={(e) => {
                      // Temporarily set value to check if it would overflow
                      const textarea = e.target;
                      const newValue = e.target.value;
                      const oldValue = formData.description;

                      textarea.value = newValue;

                      // Check if scrollHeight exceeds clientHeight (means overflow)
                      if (textarea.scrollHeight > textarea.clientHeight) {
                        // Revert - text doesn't fit
                        textarea.value = oldValue;
                        e.preventDefault();
                        return;
                      }

                      // Accept new value
                      setFormData({ ...formData, description: newValue });
                    }}
                    className="absolute inset-0 w-full h-full resize-none bg-transparent focus:outline-none text-base overflow-hidden disabled:cursor-not-allowed disabled:bg-gray-100"
                    style={{
                      padding: '8px 8px 8px calc(8px + 16px + 8px)',
                      zIndex: 2,
                      caretColor: 'black',
                      lineHeight: '1.5rem',
                      wordBreak: 'break-word'
                    }}
                    placeholder="Píšete zde... každý řádek začne bulletem"
                    disabled={isInActiveFlyer}
                  />
                </div>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Každý řádek automaticky začíná bullet pointem (•). Stiskněte Enter pro nový řádek. Zobrazí se max. 16 vizuálních řádků.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ikony produktu (max. 4)
              </label>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsIconModalOpen(true)}
                className="w-full"
                disabled={isInActiveFlyer}
              >
                <ImageIcon className="w-4 h-4 mr-2" />
                Vybrat ikony ({formData.iconIds.length}/4)
              </Button>
              {formData.iconIds.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {formData.iconIds.map((iconId) => {
                    const icon = icons.find(i => i.id === iconId);
                    if (!icon) return null;
                    return (
                      <div
                        key={iconId}
                        className="relative w-16 h-16 border-2 border-blue-500 rounded-lg p-1 bg-blue-50"
                      >
                        <img
                          src={icon.imageUrl}
                          alt={icon.name}
                          className="w-full h-full object-contain"
                          style={podkladIkony(icon)}
                          title={icon.name}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setFormData({
                              ...formData,
                              iconIds: formData.iconIds.filter(id => id !== iconId),
                            });
                          }}
                          className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white text-xs hover:bg-red-600"
                        >
                          ×
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
              <p className="mt-2 text-xs text-gray-500">
                Ikony se zobrazí v levé části obrázku produktu, zarovnané vertikálně.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Obrázek produktu {!isEdit && '*'}
              </label>
              <p className="text-xs text-gray-500 mb-2">
                Doporučený poměr: <span className="font-medium">1 : 1 (čtverec)</span>, např. {PRODUCT_IMAGE_RECOMMENDED}. V letáku se zobrazuje ve čtvercové ploše.
              </p>
              <div className="flex items-center space-x-4">
                <label className={isInActiveFlyer ? 'cursor-not-allowed' : 'cursor-pointer'}>
                  <div className={`flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-md ${isInActiveFlyer ? 'bg-gray-100 cursor-not-allowed' : 'hover:bg-gray-50'}`}>
                    <Upload className="w-4 h-4" />
                    <span>Vybrat soubor</span>
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                    disabled={isInActiveFlyer}
                  />
                </label>
                {formData.imageData && (
                  <span className="text-sm text-green-600">✓ Soubor vybrán</span>
                )}
              </div>
              <p className="mt-2 text-sm text-gray-500">
                Maximální velikost: 5MB. Podporované formáty: JPG, PNG, GIF, WebP
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Náhled v letáku (skutečná velikost)</h3>
            <p className="text-sm text-gray-600 mb-4">
              Takto bude produkt vypadat v jednom slotu letáku - zobrazeno ve skutečné velikosti.
            </p>
            <div className="border-2 border-gray-300 rounded-lg bg-gray-50 p-4 flex justify-center items-center" style={{ minHeight: '500px' }}>
              {/* Show actual slot size: 322px width, 233px height (calculated from FlyerPageView: (700-32-8)/2 - 8px padding = 322x233), zoomed to 150% */}
              <div className="bg-white rounded shadow overflow-hidden" style={{ width: '322px', height: '233px', zoom: '150%' }}>
                  <ProductFlyerLayout
                    product={{
                      id: id || 'preview',
                      name: formData.name || 'Název produktu',
                      description: formData.description || 'Zde se zobrazí popis produktu.\n\nPopis může obsahovat několik řádků s detaily o produktu, specifikacemi, výhodami a dalšími informacemi.',
                      price: formData.price,
                      originalPrice: formData.originalPrice,
                      eanCode: formData.ean,
                      brandId: formData.brandId,
                      brandName: brands.find(b => b.id === formData.brandId)?.name,
                      supplierId: '',
                      icons: formData.iconIds.map((iconId, index) => {
                        const icon = icons.find(i => i.id === iconId);
                        return icon ? {
                          id: icon.id,
                          name: icon.name,
                          imageUrl: icon.imageUrl,
                          isEnergyClass: icon.isEnergyClass,
                          useBrandColor: icon.useBrandColor,
                          position: index,
                        } : null;
                      }).filter(Boolean) as any,
                      createdAt: '',
                      updatedAt: '',
                    } as Product}
                    customImageUrl={imagePreview}
                    brandColor={brands.find(b => b.id === formData.brandId)?.color}
                  />
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-between items-center">
          <div>
            {isEdit && (
              <Button type="button" variant="outline" onClick={handleCreateCopy}>
                <Copy className="w-4 h-4 mr-2" />
                Vytvořit kopii
              </Button>
            )}
          </div>
          <div className="flex space-x-4">
            <Button type="button" variant="outline" onClick={() => navigate('/products')}>
              Zrušit
            </Button>
            <Button type="submit" isLoading={saveMutation.isPending} disabled={isInActiveFlyer}>
              <Save className="w-4 h-4 mr-2" />
              {isEdit ? 'Aktualizovat produkt' : 'Vytvořit produkt'}
            </Button>
          </div>
        </div>
      </form>

      {/* Icons Selection Modal */}
      <Modal
        isOpen={isIconModalOpen}
        onClose={() => {
          setIsIconModalOpen(false);
          setIconSearch('');
          setActiveIconTab('other');
        }}
        title="Vybrat ikony produktu"
      >
        <div className="space-y-4">
          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveIconTab('other')}
                className={`
                  whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                  ${activeIconTab === 'other'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                Ostatní ikony
              </button>
              <button
                onClick={() => setActiveIconTab('energy')}
                className={`
                  whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                  ${activeIconTab === 'energy'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                Energetické štítky
              </button>
            </nav>
          </div>

          <Input
            label="Vyhledat ikony"
            value={iconSearch}
            onChange={(e) => setIconSearch(e.target.value)}
            placeholder="Zadejte název ikony..."
          />

          {icons.length === 0 ? (
            <p className="text-sm text-gray-500 italic text-center py-8">
              Zatím nejsou k dispozici žádné ikony. Můžete je vytvořit v administraci.
            </p>
          ) : (
            <div className="grid grid-cols-4 gap-3 max-h-96 overflow-y-auto">
              {icons
                .filter(icon => {
                  // Text search filter
                  const matchesSearch = iconSearch === '' ||
                    icon.name.toLowerCase().includes(iconSearch.toLowerCase());

                  if (!matchesSearch) return false;

                  // Filtr podle zalozky: energeticke stitky zvlast, ostatni ikony zvlast.
                  if (activeIconTab === 'energy' ? !icon.isEnergyClass : icon.isEnergyClass) {
                    return false;
                  }

                  // Mekky filtr, a to jak podle znacky, tak podle kategorie:
                  // NEPRIRAZENY rozmer se nehlida a ikona projde. Co prirazene je,
                  // to sedet musi - ikona se znackou jen u produktu te znacky, ikona
                  // s kategorii jen u produktu te kategorie, ikona s obojim musi
                  // splnit obe. Ikona bez obou rozmeru je univerzalni (Akce, Novinka,
                  // Cashback, Zaruka) a nabizi se vsude - stejne jako ve starem
                  // systemu, odkud jsou kategorie prevzate.
                  //
                  // Drive tu bylo prisne pravidlo "musi sedet znacka I kategorie
                  // zaroven", ktere zalozku vyprazdnilo: znacku ma 6 ikon ze 755,
                  // takze u produktu (ten znacku ma vzdy) neprosla temer zadna.
                  const maZnacky = (icon.brands?.length ?? 0) > 0;
                  if (maZnacky) {
                    if (!formData.brandId) return false;
                    if (!icon.brands!.some(ib => ib.brand.id === formData.brandId)) return false;
                  }

                  const maKategorie = (icon.categories?.length ?? 0) > 0;
                  if (maKategorie) {
                    if (!formData.categoryId) return false;
                    if (!icon.categories!.some(ic => ic.category.id === formData.categoryId)) return false;
                  }

                  return true;
                })
                .map((icon) => {
                  const isSelected = formData.iconIds.includes(icon.id);
                  const canSelect = formData.iconIds.length < 4 || isSelected;

                  return (
                    <div
                      key={icon.id}
                      onClick={() => {
                        if (isSelected) {
                          // When removing, just filter out the icon - array stays compact
                          const newIconIds = formData.iconIds.filter(id => id !== icon.id);
                          console.log('Odebírám ikonu:', icon.id);
                          console.log('Před:', formData.iconIds);
                          console.log('Po:', newIconIds);
                          setFormData({
                            ...formData,
                            iconIds: newIconIds,
                          });
                        } else if (canSelect) {
                          // When adding, just append - array is always compact without gaps
                          const newIconIds = [...formData.iconIds, icon.id];
                          console.log('Přidávám ikonu:', icon.id);
                          console.log('Před:', formData.iconIds);
                          console.log('Po:', newIconIds);
                          setFormData({
                            ...formData,
                            iconIds: newIconIds,
                          });
                        }
                      }}
                      className={`
                        relative cursor-pointer rounded-lg border-2 p-3 transition-all
                        ${isSelected
                          ? 'border-blue-500 bg-blue-50'
                          : canSelect
                            ? 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                            : 'border-gray-200 bg-gray-100 opacity-50 cursor-not-allowed'
                        }
                      `}
                    >
                      <div className="aspect-square flex items-center justify-center">
                        <img
                          src={icon.imageUrl}
                          alt={icon.name}
                          className="max-w-full max-h-full object-contain"
                          style={podkladIkony(icon)}
                        />
                      </div>
                      <p className="mt-2 text-xs text-center text-gray-700 truncate" title={icon.name}>
                        {icon.name}
                      </p>
                      {isSelected && (
                        <div className="absolute top-1 right-1 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-xs font-bold">✓</span>
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          )}

          <div className="flex justify-between items-center pt-4 border-t">
            <span className="text-sm text-gray-600">
              Vybráno: {formData.iconIds.length}/4
            </span>
            <Button onClick={() => setIsIconModalOpen(false)}>
              Hotovo
            </Button>
          </div>
        </div>
      </Modal>

      {/* Duplicate EAN Dialog */}
      {duplicateEanDialog.isOpen && duplicateEanDialog.existingProduct && (
        <DuplicateEanDialog
          ean={formData.ean}
          existingProduct={duplicateEanDialog.existingProduct}
          productCount={duplicateEanDialog.count}
          mode={duplicateEanDialog.mode}
          onContinue={handleDuplicateEanContinue}
          onCancel={handleDuplicateEanCancel}
          onAbort={handleDuplicateEanAbort}
        />
      )}
    </div>
  );
};
